import { Col, Row } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';
import { Chip } from '@amzn/stencil-react-components/chip';

/** One chip: the stored `value`, its visible `label`, and an optional
 *  secondary `description` rendered beneath the label (two-line chip — e.g. a
 *  tag value with its group name under it). */
export interface ChipTokenItem {
  readonly value: string;
  readonly label: string;
  readonly description?: string;
}

export interface ChipTokenGroupProps {
  /** Chips to render, in order. */
  readonly items: readonly ChipTokenItem[];
  /** Called with a chip's `value` when its dismiss (×) control is used. */
  readonly onRemove: (value: string) => void;
  /** Accessible label for each chip's dismiss button, e.g. "Remove". */
  readonly dismissAltText: string;
  /** Shown (secondary text) when there are no chips. */
  readonly emptyText: string;
  readonly disabled?: boolean;
  readonly dataTestId?: string;
}

/**
 * A horizontal, wrapping group of dismissible chips — the Stencil
 * equivalent of Cloudscape's `TokenGroup`. Used to display a selected
 * value list (categories, tag values, excluded locations) where each
 * entry can be removed. Controlled: the owner holds the list and drops
 * the value on `onRemove`.
 */
export function ChipTokenGroup({
  items,
  onRemove,
  dismissAltText,
  emptyText,
  disabled = false,
  dataTestId,
}: ChipTokenGroupProps): JSX.Element {
  if (items.length === 0) {
    return (
      <Text color="color.text.secondary" {...(dataTestId ? { dataTestId } : {})}>
        {emptyText}
      </Text>
    );
  }

  return (
    <Row
      flexWrap="wrap"
      gridGap="dimensions.spacing.100"
      {...(dataTestId ? { dataTestId } : {})}
    >
      {items.map((item) => (
        <Chip
          key={item.value}
          dismissButtonAltText={`${dismissAltText} ${item.label}`}
          {...(disabled ? {} : { onDismiss: () => onRemove(item.value) })}
          {...(dataTestId ? { dataTestId: `${dataTestId}-chip-${item.value}` } : {})}
        >
          {item.description !== undefined ? (
            <Col>
              <Text>{item.label}</Text>
              <Text fontSize="T100" color="color.text.secondary">
                {item.description}
              </Text>
            </Col>
          ) : (
            item.label
          )}
        </Chip>
      ))}
    </Row>
  );
}
